import { useEffect, useState, useCallback } from "react";
import {
  fetchFiscalConfig, updateFiscalConfig,
  fetchEmittedNotes, refreshNoteStatus, cancelNote, noteDownloadUrl,
  fetchReceivedInvoices, importReceivedXml, addReceivedToStock, deleteReceivedInvoice,
  fmtMoney, fmtDate,
} from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Gear, Receipt, ArrowClockwise, XCircle, FileArrowUp, Package,
  CheckCircle, Warning, Trash, FilePdf, FileCode,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function FiscalNotes() {
  const [tab, setTab] = useState("config");
  return (
    <div data-testid="fiscal-page">
      <PageHeader
        title="Notas Fiscais"
        subtitle="Configure sua emissão fiscal, emita NFS-e a partir de OS e importe XMLs de NFes recebidas."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="config" data-testid="tab-config"><Gear size={14} className="mr-1.5" /> Configurações</TabsTrigger>
          <TabsTrigger value="emitidas" data-testid="tab-emitidas"><Receipt size={14} className="mr-1.5" /> Emitidas</TabsTrigger>
          <TabsTrigger value="recebidas" data-testid="tab-recebidas"><FileArrowUp size={14} className="mr-1.5" /> Recebidas (XML)</TabsTrigger>
        </TabsList>
        <TabsContent value="config" className="mt-6"><ConfigTab /></TabsContent>
        <TabsContent value="emitidas" className="mt-6"><EmittedTab /></TabsContent>
        <TabsContent value="recebidas" className="mt-6"><ReceivedTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ==================== CONFIG TAB ==================== */
function ConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchFiscalConfig().then(setCfg); }, []);

  if (!cfg) return <div className="text-sm text-[#475569]">Carregando...</div>;

  const setField = (k, v) => setCfg({ ...cfg, [k]: v });
  const setEndereco = (k, v) => setCfg({ ...cfg, endereco: { ...cfg.endereco, [k]: v } });

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...cfg,
        servico_iss_aliquota: parseFloat(cfg.servico_iss_aliquota || 0),
      };
      await updateFiscalConfig(payload);
      toast.success("Configuração fiscal salva");
    } catch { toast.error("Erro ao salvar"); }
    setSaving(false);
  };

  const isConfigured = cfg.token && cfg.cnpj;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Info panel */}
      <aside className="lg:col-span-1 space-y-4">
        <div className={`border rounded-md p-5 ${isConfigured ? "border-[#10B981] bg-[#10B981]/5" : "border-[#F59E0B] bg-[#F59E0B]/5"}`}>
          <div className="flex items-start gap-3">
            {isConfigured
              ? <CheckCircle size={20} weight="fill" className="text-[#10B981] mt-0.5" />
              : <Warning size={20} weight="fill" className="text-[#F59E0B] mt-0.5" />}
            <div>
              <div className="font-heading text-sm font-semibold text-[#0F172A]">
                {isConfigured ? "Configuração ativa" : "Configuração pendente"}
              </div>
              <p className="text-xs text-[#475569] mt-1">
                {isConfigured
                  ? `Ambiente: ${cfg.ambiente === "producao" ? "PRODUÇÃO" : "HOMOLOGAÇÃO"}. Você pode emitir notas.`
                  : "Preencha ao menos o token Focus NFe e o CNPJ para começar a emitir."}
              </p>
            </div>
          </div>
        </div>

        <div className="border border-[#E2E8F0] rounded-md p-5 bg-white text-xs text-[#475569] leading-relaxed space-y-3">
          <div className="font-heading text-sm text-[#0F172A]">Como obter o token</div>
          <ol className="list-decimal pl-5 space-y-1.5">
            <li>Acesse <a className="text-[#00B0F0] underline" href="https://app.focusnfe.com.br" target="_blank" rel="noreferrer">app.focusnfe.com.br</a></li>
            <li>Cadastre sua empresa (CNPJ) e envie o certificado digital A1 (.pfx)</li>
            <li>Vá em <b>Tokens</b> e gere um token para Homologação (teste) e outro para Produção</li>
            <li>Habilite NFS-e (pela prefeitura) e/ou NFe (SEFAZ) na sua conta</li>
          </ol>
          <p><b>Dica:</b> comece sempre em homologação. Os documentos emitidos não terão validade fiscal, mas permitem testar todo o fluxo.</p>
        </div>
      </aside>

      {/* Form */}
      <form onSubmit={onSave} className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-md p-6 space-y-6">
        <Section title="Conexão Focus NFe">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Token da API *</Label>
              <Input data-testid="cfg-token" type="password" placeholder="seu_token_focus_nfe"
                value={cfg.token || ""} onChange={(e) => setField("token", e.target.value)} />
            </div>
            <div>
              <Label>Ambiente *</Label>
              <Select value={cfg.ambiente} onValueChange={(v) => setField("ambiente", v)}>
                <SelectTrigger data-testid="cfg-ambiente"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="homologacao">Homologação (testes)</SelectItem>
                  <SelectItem value="producao">Produção (validade fiscal)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Section>

        <Section title="Dados da Empresa">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>CNPJ *</Label>
              <Input data-testid="cfg-cnpj" value={cfg.cnpj || ""} onChange={(e) => setField("cnpj", e.target.value.replace(/\D/g, ""))} placeholder="somente números" />
            </div>
            <div>
              <Label>Regime Tributário</Label>
              <Select value={cfg.regime_tributario} onValueChange={(v) => setField("regime_tributario", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 — Simples Nacional</SelectItem>
                  <SelectItem value="2">2 — Simples Nacional (excesso)</SelectItem>
                  <SelectItem value="3">3 — Regime Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Razão Social</Label>
              <Input value={cfg.razao_social || ""} onChange={(e) => setField("razao_social", e.target.value)} />
            </div>
            <div>
              <Label>Nome Fantasia</Label>
              <Input value={cfg.nome_fantasia || ""} onChange={(e) => setField("nome_fantasia", e.target.value)} />
            </div>
            <div>
              <Label>Inscrição Estadual</Label>
              <Input value={cfg.inscricao_estadual || ""} onChange={(e) => setField("inscricao_estadual", e.target.value)} />
            </div>
            <div>
              <Label>Inscrição Municipal</Label>
              <Input value={cfg.inscricao_municipal || ""} onChange={(e) => setField("inscricao_municipal", e.target.value)} />
            </div>
          </div>
        </Section>

        <Section title="Endereço">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-4">
              <Label>Logradouro</Label>
              <Input value={cfg.endereco?.logradouro || ""} onChange={(e) => setEndereco("logradouro", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label>Número</Label>
              <Input value={cfg.endereco?.numero || ""} onChange={(e) => setEndereco("numero", e.target.value)} />
            </div>
            <div className="md:col-span-3">
              <Label>Bairro</Label>
              <Input value={cfg.endereco?.bairro || ""} onChange={(e) => setEndereco("bairro", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label>Município</Label>
              <Input value={cfg.endereco?.municipio || ""} onChange={(e) => setEndereco("municipio", e.target.value)} />
            </div>
            <div className="md:col-span-1">
              <Label>UF</Label>
              <Input value={cfg.endereco?.uf || ""} onChange={(e) => setEndereco("uf", e.target.value.toUpperCase().slice(0, 2))} />
            </div>
            <div className="md:col-span-3">
              <Label>Código IBGE do Município (7 dígitos)</Label>
              <Input value={cfg.endereco?.codigo_municipio || ""} onChange={(e) => setEndereco("codigo_municipio", e.target.value.replace(/\D/g, ""))} placeholder="ex: 4106902 (Curitiba)" />
            </div>
            <div className="md:col-span-3">
              <Label>CEP</Label>
              <Input value={cfg.endereco?.cep || ""} onChange={(e) => setEndereco("cep", e.target.value.replace(/\D/g, ""))} placeholder="somente números" />
            </div>
          </div>
        </Section>

        <Section title="Serviço Padrão (para NFS-e)">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Código da Lista (LC 116)</Label>
              <Input value={cfg.servico_codigo || ""} onChange={(e) => setField("servico_codigo", e.target.value)} placeholder="ex: 14.01" />
            </div>
            <div>
              <Label>Código Tributário Municipal</Label>
              <Input value={cfg.servico_codigo_municipal || ""} onChange={(e) => setField("servico_codigo_municipal", e.target.value)} placeholder="ex: 140101" />
            </div>
            <div>
              <Label>Alíquota ISS (0,05 = 5%)</Label>
              <Input type="number" step="0.001" value={cfg.servico_iss_aliquota || 0} onChange={(e) => setField("servico_iss_aliquota", e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-[#475569] mt-2">
            💡 Oficinas mecânicas normalmente usam o item <b>14.01</b> da Lista de Serviços: "Lubrificação, limpeza, lustração, revisão, carga e recarga, conserto, restauração, blindagem, manutenção e conservação de máquinas, veículos, aparelhos, equipamentos, motores, elevadores ou de qualquer objeto".
          </p>
        </Section>

        <div className="flex justify-end">
          <Button type="submit" data-testid="save-cfg-btn" disabled={saving} className="bg-[#001E50] hover:bg-[#001740] text-white">
            {saving ? "Salvando..." : "Salvar configuração"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-3">{title}</div>
      {children}
    </section>
  );
}

/* ==================== EMITTED TAB ==================== */
const MIN_CANCEL_JUSTIFICATION_LENGTH = 15; // SEFAZ/prefeitura requirement

function EmittedTab() {
  const [list, setList] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [just, setJust] = useState("");

  const reload = useCallback(() => fetchEmittedNotes().then(setList), []);
  useEffect(() => { reload(); }, [reload]);

  const onRefresh = async (id) => {
    try {
      await refreshNoteStatus(id);
      toast.success("Status atualizado");
      reload();
    } catch { toast.error("Erro ao atualizar status"); }
  };

  const onCancel = async () => {
    try {
      await cancelNote(cancelTarget.id, just);
      toast.success("Nota cancelada");
      setCancelTarget(null);
      setJust("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cancelar");
    }
  };

  const statusLabel = (s) => {
    const map = {
      autorizado: ["chip chip-done", "Autorizada"],
      processando_autorizacao: ["chip chip-progress", "Processando"],
      erro_autorizacao: ["chip chip-cancel", "Erro"],
      cancelado: ["chip chip-cancel", "Cancelada"],
      erro: ["chip chip-cancel", "Erro"],
      pendente: ["chip chip-pending", "Pendente"],
    };
    const [cls, label] = map[s] || ["chip", s];
    return <span className={cls}>{label}</span>;
  };

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-md">
      {list.length === 0 ? (
        <EmptyState title="Nenhuma nota emitida" message="As NFS-e emitidas a partir das Ordens de Serviço aparecerão aqui." />
      ) : (
        <Table>
          <TableHeader className="bg-[#F8FAFC]">
            <TableRow>
              <TableHead>Referência</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Nº</TableHead>
              <TableHead>Emitida em</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((n, i) => (
              <TableRow key={n.id} className={i % 2 ? "bg-[#F8FAFC]" : ""} data-testid={`note-row-${n.id}`}>
                <TableCell className="font-mono text-xs">{n.reference}</TableCell>
                <TableCell className="uppercase text-xs font-semibold text-[#001E50]">{n.tipo}</TableCell>
                <TableCell className="font-mono">{n.numero || "—"}</TableCell>
                <TableCell className="text-[#475569] text-sm">{fmtDate(n.created_at)}</TableCell>
                <TableCell>{statusLabel(n.status)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{fmtMoney(n.valor)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => onRefresh(n.id)} title="Atualizar status" data-testid={`refresh-note-${n.id}`}>
                    <ArrowClockwise size={16} />
                  </Button>
                  {n.caminho_pdf && (
                    <a href={noteDownloadUrl(n.id, "pdf")} target="_blank" rel="noreferrer">
                      <Button variant="ghost" size="icon" title="Baixar PDF">
                        <FilePdf size={16} className="text-[#EF4444]" />
                      </Button>
                    </a>
                  )}
                  {n.caminho_xml && (
                    <a href={noteDownloadUrl(n.id, "xml")} target="_blank" rel="noreferrer">
                      <Button variant="ghost" size="icon" title="Baixar XML">
                        <FileCode size={16} className="text-[#00B0F0]" />
                      </Button>
                    </a>
                  )}
                  {n.status === "autorizado" && (
                    <Button variant="ghost" size="icon" onClick={() => setCancelTarget(n)} title="Cancelar" data-testid={`cancel-note-${n.id}`}>
                      <XCircle size={16} className="text-red-500" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Cancelar nota</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#475569]">Informe uma justificativa com no mínimo {MIN_CANCEL_JUSTIFICATION_LENGTH} caracteres. Essa informação é enviada à SEFAZ/Prefeitura.</p>
            <Textarea data-testid="cancel-just" rows={3} value={just} onChange={(e) => setJust(e.target.value)} />
            <div className="text-xs text-[#475569]">{just.length}/{MIN_CANCEL_JUSTIFICATION_LENGTH} caracteres</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>Voltar</Button>
            <Button onClick={onCancel} disabled={just.length < MIN_CANCEL_JUSTIFICATION_LENGTH} className="bg-red-600 hover:bg-red-700 text-white">
              Cancelar nota
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================== RECEIVED TAB ==================== */
function ReceivedTab() {
  const [list, setList] = useState([]);
  const [uploading, setUploading] = useState(false);

  const reload = useCallback(() => fetchReceivedInvoices().then(setList), []);
  useEffect(() => { reload(); }, [reload]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await importReceivedXml(file);
      toast.success("XML importado com sucesso");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao importar XML");
    }
    setUploading(false);
    e.target.value = "";
  };

  const onAdd = async (id) => {
    try {
      const r = await addReceivedToStock(id);
      toast.success(`${r.pecas_criadas} peças criadas, ${r.pecas_atualizadas} atualizadas no estoque.`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao adicionar ao estoque");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir esta NFe importada?")) return;
    await deleteReceivedInvoice(id);
    toast.success("Excluída");
    reload();
  };

  return (
    <div>
      <div className="bg-white border border-[#E2E8F0] rounded-md p-6 mb-4 flex items-center justify-between gap-4">
        <div>
          <div className="font-heading text-lg text-[#0F172A]">Importar XML de NFe recebida</div>
          <p className="text-sm text-[#475569] mt-1">
            Envie o arquivo XML da NFe do fornecedor. O sistema lê os itens e permite adicionar ao estoque com um clique.
          </p>
        </div>
        <label className="cursor-pointer">
          <input type="file" accept=".xml" onChange={onUpload} className="hidden" data-testid="upload-xml-input" />
          <span>
            <Button asChild className="bg-[#00B0F0] hover:bg-[#0099D6] text-white">
              <span><FileArrowUp size={16} weight="bold" className="mr-1.5" /> {uploading ? "Enviando..." : "Enviar XML"}</span>
            </Button>
          </span>
        </label>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-md">
        {list.length === 0 ? (
          <EmptyState title="Nenhuma NFe recebida importada" message="Envie o XML de uma NFe do seu fornecedor para começar." />
        ) : (
          <Table>
            <TableHeader className="bg-[#F8FAFC]">
              <TableRow>
                <TableHead>Fornecedor</TableHead>
                <TableHead>Nº / Série</TableHead>
                <TableHead>Emissão</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Itens</TableHead>
                <TableHead>Estoque</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((inv, i) => (
                <TableRow key={inv.id} className={i % 2 ? "bg-[#F8FAFC]" : ""}>
                  <TableCell>
                    <div className="font-medium">{inv.emitente_nome || "—"}</div>
                    <div className="text-xs text-[#475569] font-mono">{inv.emitente_cnpj}</div>
                  </TableCell>
                  <TableCell className="font-mono">{inv.numero}/{inv.serie}</TableCell>
                  <TableCell className="text-[#475569] text-sm">{inv.data_emissao?.slice(0, 10).split("-").reverse().join("/")}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{fmtMoney(inv.valor_total)}</TableCell>
                  <TableCell>{inv.items?.length || 0}</TableCell>
                  <TableCell>
                    {inv.items_imported
                      ? <span className="chip chip-done">Adicionado</span>
                      : <span className="chip chip-pending">Pendente</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {!inv.items_imported && (
                      <Button variant="ghost" size="icon" onClick={() => onAdd(inv.id)} title="Adicionar ao estoque" data-testid={`add-stock-${inv.id}`}>
                        <Package size={16} className="text-[#10B981]" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => onDelete(inv.id)} title="Excluir">
                      <Trash size={16} className="text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
